import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { ConfirmDialog } from '@vyce/core/src/components';
import { DeviceContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useDebounceValue } from '@vyce/core/src/hooks';
import { buildFormUrlSegment, getPaddingForContent } from '@vyce/core/src/utils';
import { SafeDndProvider } from '@vyce/core/src/components/SafeDndProvider';
import type { FormFolderDoc, FormTemplateDoc, FormTemplateFolderDoc } from '@vyce/core/src/api/types';
import { User } from '@vyce/core/src/types';

import { useFormTemplates } from './hooks/useFormTemplates';
import { TemplatesDataGrid } from './components/TemplatesDataGrid';
import { UnassignedTemplatesSection } from './components/UnassignedTemplatesSection';
import { MyFormsToolbar } from './components/MyFormsToolbar';
import { CreateFolderDialog } from './components/CreateFolderDialog';
import { ChangeFolderDialog } from './components/ChangeFolderDialog';
import { MoveFolderDialog } from './components/MoveFolderDialog';
import { RenameFolderDialog } from './components/RenameFolderDialog';
import { FolderCard } from './components/FolderCard';
import { FolderCardSkeleton } from './components/FolderCardSkeleton';

const SEARCH_DEBOUNCE_MS = 400;
const TEMPLATE_DRAG_TYPE = 'TEMPLATE_UNASSIGNED_CARD';

export const FormTemplates: React.FC<{ user: User; companyId: string }> = ({ user, companyId }) => {
  const history = useHistory();
  const location = useLocation();
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const showNotificationRef = useRef(showNotification);
  const handleServerErrorRef = useRef(handleServerError);
  const { isMobile } = useContext(DeviceContext);
  const padding = getPaddingForContent(isMobile);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounceValue(search, SEARCH_DEBOUNCE_MS);
  const [folderFilter, setFolderFilter] = useState<string>('');
  const [isArchivedView, setIsArchivedView] = useState(false);
  const [isArchivedFoldersView, setIsArchivedFoldersView] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const debouncedFolderSearch = useDebounceValue(folderSearch, SEARCH_DEBOUNCE_MS);
  const folderSearchForApi = debouncedFolderSearch === null ? '' : debouncedFolderSearch.trim();
  const isInitialMount = useRef(true);

  const isFolderView = new URLSearchParams(location.search).get('view') !== 'list';

  const {
    templates,
    loading,
    getFormTemplates,
    total,
    deleteTemplate,
    duplicateTemplate,
    createFormFromTemplate,
    createFormFromTemplateForFillIn,
    archiveTemplate,
    folders,
    setFolders,
    folderTemplatesMap,
    setFolderTemplatesMap,
    unassignedTemplates,
    setUnassignedTemplates,
    unassignedTotal,
    loadingFolderView,
    loadingUnassigned,
    getTemplateFolders,
    createFolder,
    updateFolder,
    archiveFolder,
    moveFolder,
    deleteFolder,
    addTemplateToFolder,
    reloadFoldersBoard,
    refreshUnassigned,
  } = useFormTemplates({ user, companyId });

  const [closeGridAction, setCloseGridAction] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openArchiveTemplateDialog, setOpenArchiveTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplateDoc | null>(null);
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState<string | null>(null);
  const [fillingOutTemplateId, setFillingOutTemplateId] = useState<string | null>(null);
  const [fillingInTemplateId, setFillingInTemplateId] = useState<string | null>(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingArchive, setLoadingArchive] = useState(false);

  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const [openDeleteFolderDialog, setOpenDeleteFolderDialog] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [deletingFolder, setDeletingFolder] = useState(false);

  const [openMoveFolderDialog, setOpenMoveFolderDialog] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<FormTemplateFolderDoc | null>(null);
  const [movingFolder, setMovingFolder] = useState(false);

  const [openRenameFolderDialog, setOpenRenameFolderDialog] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(false);

  const [openChangeFolderDialog, setOpenChangeFolderDialog] = useState(false);
  const [loadingChangeFolder, setLoadingChangeFolder] = useState(false);

  // suppress unused warnings while still exposing to children if needed
  void closeGridAction;
  void setCloseGridAction;

  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  useEffect(() => {
    handleServerErrorRef.current = handleServerError;
  }, [handleServerError]);

  useEffect(() => {
    isInitialMount.current = true;
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    if (isFolderView) return;
    getFormTemplates({
      search: debouncedSearch?.trim() || undefined,
      folderId: folderFilter || undefined,
      archived: isArchivedView,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isFolderView, debouncedSearch, folderFilter, isArchivedView]);

  useEffect(() => {
    if (!companyId || !isFolderView) return;
    void reloadFoldersBoard({
      archivedFolders: isArchivedFoldersView,
      folderSearch: folderSearchForApi,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isFolderView, isArchivedFoldersView, folderSearchForApi]);

  useEffect(() => {
    if (!companyId || !isFolderView) return;
    void refreshUnassigned({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isFolderView]);

  // Keep folder list available for the List View filter dropdown
  useEffect(() => {
    if (!companyId) return;
    void (async () => {
      try {
        const fetched = await getTemplateFolders({});
        setFolders(fetched);
      } catch {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const folderSkeletonCount = (() => {
    if (!loadingFolderView) return 0;
    return folders.length > 0 ? folders.length : 3;
  })();

  const folderListAsFormFolders = folders as unknown as FormFolderDoc[];

  const handleFillIn = useCallback(
    async (template: FormTemplateDoc) => {
      if (!template.id) return;
      if (!user?.email && !user?.phone) {
        showNotificationRef.current({
          message: 'Please add email or phone to your profile to use Fill in',
          options: { variant: 'error' },
        });
        return;
      }
      setFillingInTemplateId(template.id);
      const formId = await createFormFromTemplateForFillIn(template.id);
      setFillingInTemplateId(null);
      if (formId) {
        history.push(`/forms/form-to-answer/${buildFormUrlSegment(template.title, formId)}`);
      }
    },
    [createFormFromTemplateForFillIn, history, user?.email, user?.phone]
  );

  const handleSend = useCallback(
    async (template: FormTemplateDoc) => {
      if (!template.id) return;
      setFillingOutTemplateId(template.id);
      const formId = await createFormFromTemplate(template.id);
      setFillingOutTemplateId(null);
      if (formId) {
        history.push(`/forms/my-forms/${buildFormUrlSegment(template.title, formId)}`);
      }
    },
    [createFormFromTemplate, history]
  );

  const handleEditTemplate = useCallback(
    (template: FormTemplateDoc) => {
      if (!template.id) return;
      history.push(`/forms/form-templates/${buildFormUrlSegment(template.title, template.id)}/edit`);
    },
    [history]
  );

  const handleDuplicate = useCallback(
    async (template: FormTemplateDoc) => {
      if (!template.id) return;
      setDuplicatingTemplateId(template.id);
      await duplicateTemplate(template.id);
      setDuplicatingTemplateId(null);
      if (isFolderView) {
        await refreshUnassigned({});
        await reloadFoldersBoard({
          archivedFolders: isArchivedFoldersView,
          folderSearch: folderSearchForApi,
        });
      }
    },
    [
      duplicateTemplate,
      isFolderView,
      isArchivedFoldersView,
      folderSearchForApi,
      refreshUnassigned,
      reloadFoldersBoard,
    ]
  );

  const reloadAfterMutation = useCallback(async () => {
    if (isFolderView) {
      await refreshUnassigned({});
      await reloadFoldersBoard({
        archivedFolders: isArchivedFoldersView,
        folderSearch: folderSearchForApi,
      });
    } else {
      await getFormTemplates({
        search: debouncedSearch?.trim() || undefined,
        folderId: folderFilter || undefined,
        archived: isArchivedView,
      });
    }
  }, [
    isFolderView,
    isArchivedFoldersView,
    folderSearchForApi,
    refreshUnassigned,
    reloadFoldersBoard,
    getFormTemplates,
    debouncedSearch,
    folderFilter,
    isArchivedView,
  ]);

  return (
    <Box padding={padding} display="flex" flexDirection="column">
      <MyFormsToolbar
        isFolderView={isFolderView}
        isArchivedView={isArchivedView}
        isArchivedFoldersView={isArchivedFoldersView}
        folders={folderListAsFormFolders}
        onSearchChange={e => setSearch(e.target.value)}
        onFolderSearchChange={value => setFolderSearch(value)}
        onFolderFilterChange={folderId => setFolderFilter(folderId)}
        onArchivedViewChange={setIsArchivedView}
        onArchivedFoldersViewChange={setIsArchivedFoldersView}
        onFolderViewChange={checked => {
          history.push(checked ? '/forms/form-templates' : '/forms/form-templates?view=list');
        }}
        onCreateFolder={() => setOpenCreateFolderDialog(true)}
        onCreateForm={() => history.push('/forms/new-template')}
        createItemLabel="Create new template"
        itemSearchPlaceholder="Search templates..."
      />

      {isFolderView ? (
        <SafeDndProvider backend={HTML5Backend}>
          <Box minHeight="calc(100vh - 280px)">
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
              gridGap={16}
              marginBottom={3}
            >
              {loadingFolderView
                ? Array.from({ length: folderSkeletonCount }, (_, i) => (
                    <FolderCardSkeleton key={`folder-skeleton-${i}`} />
                  ))
                : folders.map(folder => {
                    const folderAsFormFolder = {
                      ...folder,
                      formsCount: folder.templatesCount,
                    } as unknown as FormFolderDoc;
                    return (
                      <FolderCard
                        key={folder.id}
                        folder={folderAsFormFolder}
                        dragType={TEMPLATE_DRAG_TYPE}
                        countLabel="Templates"
                        onDrop={async (item, targetFolderId) => {
                          const dragged = item as FormTemplateDoc;
                          if (!dragged?.id) return;
                          const previousUnassigned = [...unassignedTemplates];
                          const previousFolderTemplates = [...(folderTemplatesMap[targetFolderId] || [])];
                          setUnassignedTemplates(prev => prev.filter(t => t.id !== dragged.id));
                          setFolderTemplatesMap(prev => ({
                            ...prev,
                            [targetFolderId]: [...(prev[targetFolderId] || []), dragged],
                          }));
                          try {
                            await addTemplateToFolder(dragged.id, targetFolderId);
                            await refreshUnassigned({});
                            await reloadFoldersBoard({
                              archivedFolders: isArchivedFoldersView,
                              folderSearch: folderSearchForApi,
                            });
                          } catch (error) {
                            setUnassignedTemplates(previousUnassigned);
                            setFolderTemplatesMap(prev => ({
                              ...prev,
                              [targetFolderId]: previousFolderTemplates,
                            }));
                            showNotificationRef.current({
                              message: 'Failed to move template to folder',
                              options: { variant: 'error' },
                            });
                          }
                        }}
                        onDeleteFolder={folderId => {
                          setSelectedFolderId(folderId);
                          setOpenDeleteFolderDialog(true);
                        }}
                        onMoveFolderToFolder={async (sourceFolderId, targetFolderId) => {
                          try {
                            await moveFolder(sourceFolderId, targetFolderId);
                            showNotificationRef.current({
                              message: 'Folder moved',
                              options: { variant: 'success' },
                            });
                            await reloadFoldersBoard({
                              archivedFolders: isArchivedFoldersView,
                              folderSearch: folderSearchForApi,
                            });
                          } catch (error) {
                            handleServerErrorRef.current(error);
                          }
                        }}
                        onMoveFolder={target => {
                          setSelectedFolder(target as unknown as FormTemplateFolderDoc);
                          setOpenMoveFolderDialog(true);
                        }}
                        onRenameFolder={target => {
                          setSelectedFolder(target as unknown as FormTemplateFolderDoc);
                          setOpenRenameFolderDialog(true);
                        }}
                        onArchiveFolder={async target => {
                          try {
                            await archiveFolder(target.id, target.archived !== true);
                            showNotificationRef.current({
                              message: target.archived !== true ? 'Folder archived' : 'Folder unarchived',
                              options: { variant: 'success' },
                            });
                            await reloadFoldersBoard({
                              archivedFolders: isArchivedFoldersView,
                              folderSearch: folderSearchForApi,
                            });
                          } catch (error) {
                            handleServerErrorRef.current(error);
                          }
                        }}
                        onColourFolder={async (folderId, colour) => {
                          const previousColour = folders.find(f => f.id === folderId)?.colour ?? null;
                          setFolders(prev =>
                            prev.map(f => (f.id === folderId ? { ...f, colour } : f))
                          );
                          try {
                            await updateFolder(folderId, { colour });
                          } catch (error) {
                            setFolders(prev =>
                              prev.map(f => (f.id === folderId ? { ...f, colour: previousColour } : f))
                            );
                            handleServerErrorRef.current(error);
                          }
                        }}
                        onOpenFolder={() =>
                          history.push(
                            `/forms/form-templates/folders/${buildFormUrlSegment(folder.name, folder.id)}`
                          )
                        }
                      />
                    );
                  })}
              {!loadingFolderView && folders.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No Folders
                </Typography>
              )}
            </Box>

            <UnassignedTemplatesSection
              rows={unassignedTemplates}
              dragType={TEMPLATE_DRAG_TYPE}
              withActions
              isArchivedView={isArchivedView}
              fillingInTemplateId={fillingInTemplateId}
              fillingOutTemplateId={fillingOutTemplateId}
              duplicatingTemplateId={duplicatingTemplateId}
              onFillIn={handleFillIn}
              onSend={handleSend}
              onEditTemplate={handleEditTemplate}
              onDuplicateTemplate={handleDuplicate}
              onArchiveTemplate={template => {
                setSelectedTemplate(template);
                setOpenArchiveTemplateDialog(true);
              }}
              onChangeFolder={template => {
                setSelectedTemplate(template);
                setOpenChangeFolderDialog(true);
              }}
              onDeleteTemplate={template => {
                setSelectedTemplate(template);
                setOpenDeleteDialog(true);
              }}
              loading={loadingUnassigned}
              total={unassignedTotal}
              onSearchChange={e => setSearch(e.target.value)}
            />
          </Box>
        </SafeDndProvider>
      ) : (
        <TemplatesDataGrid
          rows={templates}
          withActions
          isArchivedView={isArchivedView}
          fillingInTemplateId={fillingInTemplateId}
          fillingOutTemplateId={fillingOutTemplateId}
          duplicatingTemplateId={duplicatingTemplateId}
          onFillIn={handleFillIn}
          onSend={handleSend}
          onEditTemplate={handleEditTemplate}
          onDuplicateTemplate={handleDuplicate}
          onArchiveTemplate={template => {
            setSelectedTemplate(template);
            setOpenArchiveTemplateDialog(true);
          }}
          onChangeFolder={template => {
            setSelectedTemplate(template);
            setOpenChangeFolderDialog(true);
          }}
          onDeleteTemplate={template => {
            setSelectedTemplate(template);
            setOpenDeleteDialog(true);
          }}
          height="calc(100vh - 240px)"
          loading={loading}
          total={total}
          setOffset={() => {}}
        />
      )}

      <ConfirmDialog
        open={openDeleteDialog}
        handleClose={() => {
          if (!loadingDelete) {
            setOpenDeleteDialog(false);
            setSelectedTemplate(null);
          }
        }}
        handleConfirm={async () => {
          if (selectedTemplate?.id) {
            setLoadingDelete(true);
            await deleteTemplate(selectedTemplate.id);
            setLoadingDelete(false);
            setOpenDeleteDialog(false);
            setSelectedTemplate(null);
            await reloadAfterMutation();
          }
        }}
        title="Are you sure you want to delete this form template?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={loadingDelete}
      />

      <ConfirmDialog
        open={openArchiveTemplateDialog}
        handleClose={() => {
          if (!loadingArchive) {
            setOpenArchiveTemplateDialog(false);
            setSelectedTemplate(null);
          }
        }}
        handleConfirm={async () => {
          if (!selectedTemplate?.id) return;
          setLoadingArchive(true);
          await archiveTemplate(selectedTemplate.id, selectedTemplate.archived !== true);
          setLoadingArchive(false);
          setOpenArchiveTemplateDialog(false);
          setSelectedTemplate(null);
          await reloadAfterMutation();
        }}
        title={
          selectedTemplate?.archived
            ? 'Are you sure you want to unarchive this template?'
            : 'Are you sure you want to archive this template?'
        }
        confirmText={selectedTemplate?.archived ? 'Unarchive' : 'Archive'}
        cancelText="Cancel"
        loading={loadingArchive}
      />

      <ConfirmDialog
        open={openDeleteFolderDialog}
        handleClose={() => {
          setOpenDeleteFolderDialog(false);
          setSelectedFolderId(null);
        }}
        handleConfirm={async () => {
          if (!selectedFolderId) return;
          try {
            setDeletingFolder(true);
            await deleteFolder(selectedFolderId, 'cascade');
            setOpenDeleteFolderDialog(false);
            setSelectedFolderId(null);
            await reloadFoldersBoard({
              archivedFolders: isArchivedFoldersView,
              folderSearch: folderSearchForApi,
            });
            await refreshUnassigned({});
          } finally {
            setDeletingFolder(false);
          }
        }}
        title="Are you sure you want to delete this folder?"
        subtitle="All nested folders inside this folder will also be deleted."
        loading={deletingFolder}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ChangeFolderDialog
        open={openChangeFolderDialog}
        loading={loadingChangeFolder}
        companyId={companyId}
        userId={user.uuid}
        kind="template"
        onClose={() => {
          setOpenChangeFolderDialog(false);
          setSelectedTemplate(null);
        }}
        onConfirm={async targetFolderId => {
          if (!selectedTemplate?.id || !targetFolderId) return;
          try {
            setLoadingChangeFolder(true);
            await addTemplateToFolder(selectedTemplate.id, targetFolderId);
            showNotificationRef.current({
              message: 'Template moved to another folder',
              options: { variant: 'success' },
            });
            setOpenChangeFolderDialog(false);
            setSelectedTemplate(null);
            await reloadAfterMutation();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setLoadingChangeFolder(false);
          }
        }}
      />

      <MoveFolderDialog
        open={openMoveFolderDialog}
        loading={movingFolder}
        companyId={companyId}
        userId={user.uuid}
        kind="template"
        folderId={selectedFolder?.id ?? null}
        currentParentId={selectedFolder?.parentId ?? null}
        onClose={() => {
          setOpenMoveFolderDialog(false);
          setSelectedFolder(null);
        }}
        onConfirm={async newParentId => {
          if (!selectedFolder) return;
          try {
            setMovingFolder(true);
            await moveFolder(selectedFolder.id, newParentId);
            showNotificationRef.current({
              message: 'Folder moved',
              options: { variant: 'success' },
            });
            setOpenMoveFolderDialog(false);
            setSelectedFolder(null);
            await reloadFoldersBoard({
              archivedFolders: isArchivedFoldersView,
              folderSearch: folderSearchForApi,
            });
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setMovingFolder(false);
          }
        }}
      />

      <RenameFolderDialog
        open={openRenameFolderDialog}
        initialName={selectedFolder?.name ?? ''}
        loading={renamingFolder}
        onClose={() => {
          setOpenRenameFolderDialog(false);
          setSelectedFolder(null);
        }}
        onConfirm={async name => {
          if (!selectedFolder) return;
          try {
            setRenamingFolder(true);
            await updateFolder(selectedFolder.id, { name });
            showNotificationRef.current({
              message: 'Folder renamed',
              options: { variant: 'success' },
            });
            setOpenRenameFolderDialog(false);
            setSelectedFolder(null);
            await reloadFoldersBoard({
              archivedFolders: isArchivedFoldersView,
              folderSearch: folderSearchForApi,
            });
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setRenamingFolder(false);
          }
        }}
      />

      <CreateFolderDialog
        open={openCreateFolderDialog}
        folderName={newFolderName}
        loading={creatingFolder}
        onFolderNameChange={setNewFolderName}
        onClose={() => setOpenCreateFolderDialog(false)}
        onCreate={async colour => {
          if (!newFolderName.trim()) return;
          try {
            setCreatingFolder(true);
            await createFolder(newFolderName.trim(), colour, null);
            setNewFolderName('');
            setOpenCreateFolderDialog(false);
            await reloadFoldersBoard({
              archivedFolders: isArchivedFoldersView,
              folderSearch: folderSearchForApi,
            });
            if (!isFolderView) {
              const refreshed = await getTemplateFolders({});
              setFolders(refreshed);
            }
          } finally {
            setCreatingFolder(false);
          }
        }}
      />
    </Box>
  );
};
